import { useState } from "react";
import { useSettings } from "../context/SettingsContext";
import { Bookmark, Highlighter, Volume2, Share2, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import {
  isBookmarked, toggleBookmark, getHighlight, setHighlight,
} from "../lib/storage";
import { shareVerse } from "../lib/shareVerse";
import { tokenizeCharacters } from "../lib/characterLinks";

const HL_COLORS = [
  { key: "gold", css: "rgba(212,175,55,0.30)" },
  { key: "green", css: "rgba(92,138,110,0.32)" },
  { key: "blue", css: "rgba(110,143,176,0.32)" },
  { key: "rose", css: "rgba(192,86,59,0.30)" },
];

function VerseText({ text, onCharacter }) {
  if (!onCharacter) return text;
  return tokenizeCharacters(text).map((part, i) =>
    typeof part === "string" ? (
      part
    ) : (
      <span
        key={i}
        role="button"
        tabIndex={0}
        data-testid="char-link"
        onClick={(e) => { e.stopPropagation(); onCharacter(part.cid, part.token); }}
        className="cursor-pointer underline decoration-dotted decoration-primary/50 underline-offset-4 transition-colors hover:decoration-primary hover:text-primary"
      >
        {part.token}
      </span>
    )
  );
}

export default function VerseBlock({ book, bookName, chapter, verse, onSpeak, active, innerRef, onCharacter, onExplain }) {
  const { focusMode, fontScale, theme, translation } = useSettings();
  const [open, setOpen] = useState(false);
  const [marked, setMarked] = useState(isBookmarked(book, chapter, verse.v));
  const [hl, setHl] = useState(getHighlight(book, chapter, verse.v));

  const dim = focusMode && !active;

  const handleBookmark = () => {
    const on = toggleBookmark({ book, bookName, chapter, verse: verse.v, text: verse.t });
    setMarked(on);
    toast(on ? "Saved to your verses" : "Removed from saved verses");
  };
  const handleShare = async () => {
    const res = await shareVerse({ bookName, chapter, verse: verse.v, text: verse.t, translation });
    if (res === "copied") toast("Verse copied — paste it anywhere");
  };
  const handleHighlight = (color) => {
    const next = hl === color ? null : color;
    setHighlight(book, chapter, verse.v, next);
    setHl(next);
  };

  return (
    <div
      ref={innerRef}
      data-testid={`verse-${verse.v}`}
      data-active={active ? "true" : "false"}
      onClick={() => setOpen((o) => !o)}
      className={`reading-text group relative -mx-3 cursor-pointer select-text rounded-2xl px-3 py-1.5 transition-all duration-300 ease-out ${
        open ? "bg-primary/[0.07] ring-1 ring-primary/40" : ""
      }`}
      style={{
        opacity: dim ? (theme === "dark" ? 0.18 : 0.34) : 1,
        filter: dim ? "blur(1px)" : "none",
        transform: focusMode && active ? "scale(1.012)" : "scale(1)",
      }}
    >
      <p
        className="leading-relaxed"
        style={{
          fontSize: `${1.6 * fontScale}rem`,
          lineHeight: 1.7,
          fontWeight: active ? 600 : 500,
          color: verse.j ? undefined : theme === "dark" ? undefined : "#0c0c0d",
          background: hl ? HL_COLORS.find((c) => c.key === hl)?.css : "transparent",
          borderRadius: hl ? "0.4rem" : 0,
          padding: hl ? "0.1rem 0.4rem" : 0,
          margin: hl ? "-0.1rem -0.4rem" : 0,
          boxDecorationBreak: "clone",
          WebkitBoxDecorationBreak: "clone",
        }}
      >
        <sup
          className="font-ui mr-2 align-super text-[0.62em] font-bold tracking-wider text-primary"
          style={{ opacity: 0.85 }}
        >
          {verse.v}
        </sup>
        <span
          style={
            verse.j
              ? {
                  color: theme === "dark" ? "#d11d3c" : "#9e1b32",
                  fontWeight: 800,
                  textShadow:
                    theme === "dark"
                      ? "0 1px 2px rgba(0,0,0,0.85), 0 0 12px rgba(0,0,0,0.6)"
                      : "0 1px 2px rgba(0,0,0,0.35), 0 0 10px rgba(255,255,255,0.45)",
                }
              : undefined
          }
        >
          <VerseText text={verse.t} onCharacter={onCharacter} />
        </span>
        {marked && (
          <Bookmark className="ml-2 inline h-4 w-4 fill-primary text-primary align-middle" />
        )}
      </p>

      {/* inline verse actions */}
      {open && (
        <div
          className="font-ui mt-3 flex items-center gap-2 rounded-full border border-border bg-popover/90 px-2 py-1.5 shadow-lg backdrop-blur-md w-fit float-up"
          onClick={(e) => e.stopPropagation()}
          data-testid={`verse-actions-${verse.v}`}
        >
          <button
            onClick={handleBookmark}
            data-testid={`bookmark-btn-${verse.v}`}
            className="flex h-7 w-7 items-center justify-center rounded-full hover:bg-muted transition-colors"
            title={marked ? "Saved — tap to remove" : "Save verse"}
          >
            <Bookmark className={`h-4 w-4 ${marked ? "fill-primary text-primary" : ""}`} />
          </button>
          <button
            onClick={handleShare}
            data-testid={`share-verse-${verse.v}`}
            className="flex h-7 w-7 items-center justify-center rounded-full hover:bg-muted transition-colors"
            title="Share verse"
          >
            <Share2 className="h-4 w-4" />
          </button>
          <button
            onClick={() => onSpeak?.(`Verse ${verse.v}. ${verse.t}`)}
            data-testid={`speak-verse-${verse.v}`}
            className="flex h-7 w-7 items-center justify-center rounded-full hover:bg-muted transition-colors"
            title="Read aloud"
          >
            <Volume2 className="h-4 w-4" />
          </button>
          {onExplain && (
            <button
              onClick={() => onExplain(verse.v)}
              data-testid={`explain-verse-${verse.v}`}
              className="flex h-7 items-center gap-1 rounded-full bg-primary/10 px-2.5 text-xs font-semibold text-primary hover:bg-primary/20 transition-colors"
              title="Explain this verse"
            >
              <GraduationCap className="h-3.5 w-3.5" /> Explain
            </button>
          )}
          <span className="mx-1 h-4 w-px bg-border" />
          <Highlighter className="h-3.5 w-3.5 text-muted-foreground" />
          {HL_COLORS.map((c) => (
            <button
              key={c.key}
              onClick={() => handleHighlight(c.key)}
              data-testid={`highlight-${c.key}-${verse.v}`}
              className={`h-5 w-5 rounded-full border transition-transform hover:scale-110 ${
                hl === c.key ? "ring-2 ring-foreground ring-offset-1 ring-offset-popover" : "border-border"
              }`}
              style={{ background: c.css }}
              title={`Highlight ${c.key}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
